import { useState, lazy, Suspense, useEffect, useRef } from "react";
import { Award, Users, Clock, Sparkles, LeafyGreenIcon } from "lucide-react";
import Sliders from "./Sliders.jsx";
import FnQ from "./components/fnq/FnQ.jsx";

// Lazy imports (code splitting)
const Heading = lazy(() => import("../../../pages/Frontend/utils/Heading.jsx"));
const HeroSection = lazy(() => import("./components/HeroSection.jsx"));
const ProductsSection = lazy(() => import("./components/ProductsSection.jsx"));
const Testimony = lazy(() => import("./components/slider/Slider"));
const AboutSection = lazy(() => import("./components/aboutUs/AboutSection.jsx"));

/* =========================
   Intersection Observer Hook (Optimized)
========================= */
const useInViewOnce = (threshold = 0.1) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect(); // load once
        }
      },
      { threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  return [ref, visible];
};

/* =========================
   Values Data (Accessibility: changed to <h2> and <h3>)
========================= */
const values = [
   {
    icon: LeafyGreenIcon,
    title: "Green Guard ​",
    description:
      "Eco-conscious window solutions designed to reduce heat gain, improve energy efficiency, and create more comfortable, sustainable spaces.",
  },
  {
    icon: Award,
    title: "Versatile Product Range ",
    description:
      "From classic roller and Roman Blinds to energy-efficient honeycombs systems , vertical formats , motorized tracks , and enclosed glass solutions, we manufacture 13 types of blinds and serve varied architectural and technical requirements",
  },
  {
    icon: Users,
    title: "Engineered for Performance  ",
    description:
      "Products are designed for durability, easy maintenance, smooth operation, and longterm reliability, even in demanding environments like healthcare and commercial spaces.",
  },
  {
    icon: Clock,
    title: "Factory-to-Home Quality ",
    description:
      "Because we operate without retail middlemen, customers benefit from direct pricing, complete quality control, and products crafted to the highest standards.",
  },
  {
    icon: Sparkles,
    title: "Project-Ready Capability ​",
   description:
  "Thoughtfully designed to improve energy efficiency, reduce heat gain, and create more sustainable spaces while maintaining comfort, style, and performance.", },
 
];

/* =========================
   Component
========================= */
function HomePage() {
  const [expandedValues, setExpandedValues] = useState({});
  const [showVideo, setShowVideo] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const toggleValueDescription = (index) => {
    setExpandedValues((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const truncateText = (text, maxLength = 100) =>
    text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

  /* Lazy load sections on scroll with higher threshold for better UX */
  const [heroRef, showHero] = useInViewOnce(0.01);
  const [productRef, showProducts] = useInViewOnce(0.01);
  const [aboutRef, showAbout] = useInViewOnce(0.01);
  const [testimonyRef, showTestimony] = useInViewOnce(0.01);
  const [fnqRef, showFnq] = useInViewOnce(0.01);

  // Load video after image is loaded to improve LCP
  useEffect(() => {
    // Preload the image
    const img = new Image();
    img.src = "https://wn-new-collections-development.zohostratus.in/hero_image.jpg";
    img.onload = () => {
      setImageLoaded(true);
      // Load video after image is loaded
      const loadVideo = () => {
        setShowVideo(true);
      };
      // Small delay after image load to ensure smooth transition
      setTimeout(loadVideo, 300);
    };

    // Fallback: if image takes too long, load video anyway after 3 seconds
    const timeoutId = setTimeout(() => {
      if (!imageLoaded) {
        setShowVideo(true);
      }
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [imageLoaded]);

  return (
    <div className="overflow-x-hidden">
      {/* ===== HERO VIDEO / IMAGE ===== */}
   <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                            <Sliders className="w-full h-[250px] sm:h-[300px] md:h-[350px] lg:h-[400px] xl:h-[650px] object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
                        </div>

      {/* HERO */}
      <div ref={heroRef}>
        {showHero && (
          <Suspense
            fallback={
              <div className="text-center py-10" aria-label="Loading Hero Section">
                Loading...
              </div>
            }
          >
            <HeroSection />
          </Suspense>
        )}
      </div>

      {/* PRODUCTS */}
      <div ref={productRef}>
        {showProducts && (
          <Suspense
            fallback={
              <div className="text-center py-10" aria-label="Loading Products Section">
                Loading...
              </div>
            }
          >
            <ProductsSection />
          </Suspense>
        )}
      </div>

         <main className="">
        <div className="flex-1 relative w-full bg-[#F8F5F0]">
          {/* Desktop Video/Image Container */}
          <div className="hidden md:block relative w-full lg:h-[90vh] md:h-[50vh] drop-shadow-2xl overflow-hidden">
            {/* Show image first */}
            <img
              src="https://wn-new-collections-development.zohostratus.in/hero_image.jpg"
              alt="Hero background"
              className={`absolute top-0 left-0 w-full h-full object-cover h-[60vh] md:h-[60vh] lg:h-[90vh] transition-opacity duration-700 ${
                showVideo ? 'opacity-0' : 'opacity-100'
              }`}
              loading="eager"
              fetchpriority="high"
            />

            {/* Video loads after image */}
            {showVideo && (
              <video
                src="https://wn-new-collections-development.zohostratus.in/heroVideo_2.mp4"
                autoPlay
                muted
                loop
                playsInline
                aria-hidden="true"
                className={`absolute top-0 left-0 w-full h-full object-cover h-[60vh] md:h-[60vh] lg:h-[90vh] transition-opacity duration-700 ${
                  showVideo ? 'opacity-100' : 'opacity-0'
                }`}
                poster="https://wn-new-collections-development.zohostratus.in/hero_image.jpg"
              />
            )}
          </div>

          {/* Mobile Video/Image - Improved performance */}
          <div className="md:hidden relative w-full h-[50vh] sm:h-[60vh] bg-[#F8F5F0]">
            {/* Show image first on mobile */}
            <img
              src="https://wn-new-collections-development.zohostratus.in/hero_image.jpg"
              alt="Hero background"
              className={`w-full h-[50vh] sm:h-[60vh] object-cover transition-opacity duration-700 ${
                showVideo ? 'opacity-0' : 'opacity-100'
              }`}
              loading="eager"
              fetchpriority="high"
            />

            {showVideo && (
              <video
                src="https://wn-new-collections-development.zohostratus.in/heroVideo_2.mp4"
                autoPlay
                muted
                loop
                playsInline
                aria-hidden="true"
                className={`absolute top-0 left-0 w-full object-cover h-[50vh] sm:h-[60vh] transition-opacity duration-700 ${
                  showVideo ? 'opacity-100' : 'opacity-0'
                }`}
                poster="https://wn-new-collections-development.zohostratus.in/hero_image.jpg"
                style={{
                  maskImage: "linear-gradient(to top, black 95%, transparent 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to top, black 75%, transparent 100%)",
                }}
              />
            )}
          </div>
        </div>
      </main>

      {/* ABOUT */}
      <div ref={aboutRef}>
        {showAbout && (
          <Suspense
            fallback={
              <div className="text-center py-10" aria-label="Loading About Section">
                Loading...
              </div>
            }
          >
            <AboutSection />
          </Suspense>
        )}
      </div>

    

      {/* ===== VALUES GRID ===== */}
      <div className="bg-gradient-to-b from-[#F8F5F0] via-[#F2EDE5] to-[#F8F5F0] py-12 transition-all duration-700">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <Heading text={"Why Choose Us "} className="!pb-2 sm:!pb-0" />
            <hr className="heading-seperator mx-auto border-t-4 border-[#66944b] opacity-90 w-16 sm:w-20 md:w-24 h-0.5 !mb-3 lg:!mb-0 lg:mt-2 md:mt-2 md:!mb-4 transition-all duration-500 hover:w-32" />
            <p className="text-[#661E33]/80 font-medium text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-2">
              Our advantage lies in being manufacturers first. Customization,
              quality control, and execution are built into our process.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5 md:gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              const isExpanded = expandedValues[index];

              return (
                <div
                  key={value.title}
                  className="group bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-[#D1BC9F]/30 hover:border-[#661E33]/40 hover:bg-gradient-to-b hover:from-white hover:to-[#FAF7F3]"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#F8F5F0] border border-[#D1BC9F]/40 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#661E33] group-hover:border-[#661E33] group-hover:rotate-6 group-hover:scale-110 transition-all duration-500 shadow-sm">
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#661E33] group-hover:text-white transition-colors duration-500" />
                  </div>

                  <h3 className="text-base sm:text-lg text-[#661E33] mb-2 font-bold tracking-tight">
                    {value.title}
                  </h3>

                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    {isExpanded ? value.description : truncateText(value.description)}
                  </p>

                  {value.description.length > 100 && (
                    <button
                      onClick={() => toggleValueDescription(index)}
                      className="text-sm sm:text-base font-semibold text-[#661E33] hover:text-[#D1BC9F] mt-2 transition-colors duration-300"
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? "Read Less" : "Read More"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      

      {/* TESTIMONY */}
      <div ref={testimonyRef}>
        {showTestimony && (
          <Suspense
            fallback={
              <div className="text-center py-10" aria-label="Loading Testimonials">
                Loading...
              </div>
            }
          >
            <Testimony />
          </Suspense>
        )}
      </div>
      <div ref={fnqRef}>
        {showFnq && (
          <Suspense
            fallback={
              <div className="text-center py-10" aria-label="Loading Testimonials">
                Loading...
              </div>
            }
          >
            <FnQ />
          </Suspense>
        )}
      </div>
    </div>
  );
}

export default HomePage;